import { Box, Stack, Typography } from '@mui/material';
import { QuestionMarkCircle, WarningFilled } from '@vy/ui-design-system';
import type { ProductConsumption } from '@vy/types';
import { formatNumber } from '@vy/utils';
import React from 'react';
import { Status } from '../../../../../components/design/statuses/Status/Status';
import { VyLinearProgress } from '../../../../../components/new-design/data-display/VyLinearProgress/VyLinearProgress';
import { VyTooltip } from '../../../../../components/new-design/VyTooltip';
import { CUSTOMER_PORTAL_TEXT_COLOR } from '../customer-portal-colors';

const COMMITMENT_PROGRESS_COLOR = '#06B6D4';

type ProductCommitmentProgressBarsProps = {
  commitments: ProductConsumption[];
};

export const ProductCommitmentProgressBars: React.FC<ProductCommitmentProgressBarsProps> = ({ commitments }) => {
  return (
    <Stack spacing={1} sx={{ marginBottom: '12px' }}>
      {commitments.map((product) => {
        const isOverage = product.consumedAmount > product.provisionedAmount;
        const usagePercent = ((product.consumedAmount / product.provisionedAmount) * 100).toFixed(2);

        return (
          <Box key={product.productId} sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Typography
              variant="body2"
              sx={{
                color: CUSTOMER_PORTAL_TEXT_COLOR,
                fontSize: '16px',
                fontWeight: 500,
                fontFamily: 'Inter, sans-serif',
                whiteSpace: 'nowrap',
              }}
            >
              {product.productName}:
            </Typography>
            <Box sx={{ maxWidth: 240 }}>
              <VyLinearProgress
                currentValue={product.consumedAmount}
                totalValue={product.provisionedAmount}
                progressColor={COMMITMENT_PROGRESS_COLOR}
                renderLabel={() => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: CUSTOMER_PORTAL_TEXT_COLOR,
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif',
                        width: '100%',
                      }}
                    >
                      <strong>{formatNumber(product.consumedAmount)}</strong> /{' '}
                      {formatNumber(product.provisionedAmount)} ({usagePercent}%)
                    </Typography>
                    <VyTooltip
                      placement="top"
                      tooltipContent={
                        <>
                          {product.productName}: You've used {formatNumber(product.consumedAmount)} out of
                          the {formatNumber(product.provisionedAmount)}
                          <br />
                          committed for this billing period
                        </>
                      }
                    >
                      <Box component="span" sx={{ display: 'flex', cursor: 'pointer' }}>
                        <QuestionMarkCircle />
                      </Box>
                    </VyTooltip>
                  </Box>
                )}
              />
            </Box>
            {isOverage && (
              <Status
                status="ERROR"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'default !important',
                  columnGap: '8px',
                  whiteSpace: 'nowrap',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                <WarningFilled style={{ transform: 'scale(0.8)' }} />
                Overage
              </Status>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};
